import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";

const TRANSACTION_TYPE_LABELS = {
  1: "Vendor",
  2: "Eksport",
  3: "Shërbime jashtë vendit",
};

function formatMoney(value) {
  return Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateValue(value) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-GB");
  }

  return String(value);
}

function MetaItem({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "160px 1fr",
        gap: 1.25,
        alignItems: "start",
        py: 1.1,
        borderBottom: "1px dashed #dce7ee",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "#5d7385",
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "#11263a", fontWeight: 600, wordBreak: "break-word" }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
}

function SectionCard({ title, children }) {
  return (
    <Box
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: "1px solid #dce8ef",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(12, 35, 64, 0.05)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: "#1e5560",
          fontWeight: 800,
          mb: 1.5,
          letterSpacing: 0.2,
          textTransform: "uppercase",
          fontSize: "0.76rem",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function AdvanceViewModal({ show, onHide, data, loading = false }) {
  const title = data?.InvoiceNr
    ? `Detajet e Avansit #${data.InvoiceNr}`
    : "Detajet e Avansit";

  const paymentLabel = (() => {
    if (data?.PaymentOptions === 0 || data?.PaymentOptions === "0") {
      return "Pagesa Behet më vonë";
    }

    return data?.PaymentTypeName || data?.PaymentType || "-";
  })();

  const transactionTypeLabel =
    TRANSACTION_TYPE_LABELS[Number(data?.TransactionType)] || "-";

  const amount0 = Number(data?.Amount0 || 0);
  const amount8 = Number(data?.Amount8 || 0);
  const amount18 = Number(data?.Amount18 || 0);
  const vat8 = Number(data?.VAT8 || 0);
  const vat18 = Number(data?.VAT18 || 0);
  const total0 = Number(data?.Total0 || amount0);
  const total8 = Number(data?.Total8 || amount8 + vat8);
  const total18 = Number(data?.Total18 || amount18 + vat18);
  const totalNoVat = Number(data?.AmountWithoutVAT || amount0 + amount8 + amount18);
  const totalVat = Number(data?.VATTotal || vat8 + vat18);
  const totalWithVat = Number(data?.Total || totalNoVat + totalVat);

  const paymentStatusPaid = !(data?.PaymentOptions === 0 || data?.PaymentOptions === "0");

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: "#f3f8fb",
        },
      }}
    >
      <Box
        sx={{
          px: 3.5,
          py: 2.75,
          background: "linear-gradient(135deg, #15324b 0%, #24605c 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.4 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Shikim i të dhënave pa mundësi ndryshimi
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={paymentStatusPaid ? "Paguar" : "Pa Paguar"}
            color={paymentStatusPaid ? "success" : "error"}
            variant="filled"
            sx={{ fontWeight: 800 }}
          />
          <Chip
            label={`Lloji: ${transactionTypeLabel}`}
            sx={{
              fontWeight: 700,
              backgroundColor: "rgba(255,255,255,0.16)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.24)",
            }}
          />
        </Box>
      </Box>

      <DialogContent dividers sx={{ p: 3.5, backgroundColor: "#f3f8fb" }}>
        {loading ? (
          <Box
            sx={{
              minHeight: 260,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1.2,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ color: "#4b5f71" }}>
              Duke marrë të dhënat e avansit...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 2 }}>
            <SectionCard title="Detaje të Dokumentit">
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                  gap: 1.4,
                  mb: 1.7,
                }}
              >
                {[
                  { label: "Numri i Avansit", value: data?.InvoiceNr || "-" },
                  { label: "Data e Dokumentit", value: formatDateValue(data?.Date) },
                  { label: "Klienti", value: data?.AccountName || "-" },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.45,
                      borderRadius: 2,
                      border: "1px solid #dce8ef",
                      backgroundColor: "#f8fcff",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#607485",
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        textTransform: "uppercase",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.45,
                        color: "#11263a",
                        fontWeight: 700,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.2 }}>
                <Box
                  sx={{
                    border: "1px solid #dce8ef",
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    px: 1.5,
                    py: 0.45,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#5d7385",
                      fontWeight: 800,
                      letterSpacing: 0.2,
                      textTransform: "uppercase",
                      display: "block",
                      pt: 1,
                    }}
                  >
                    Informata Kontabël
                  </Typography>
                  <MetaItem label="Llogaria Kontabël" value="702-Te Hyrat e Tjera" />
                  <MetaItem label="Lloji i Transaksionit" value={transactionTypeLabel} />
                  <MetaItem label="Data e Pagesës" value={formatDateValue(data?.PayDate)} />
                </Box>

                <Box
                  sx={{
                    border: "1px solid #dce8ef",
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    px: 1.5,
                    py: 0.45,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#5d7385",
                      fontWeight: 800,
                      letterSpacing: 0.2,
                      textTransform: "uppercase",
                      display: "block",
                      pt: 1,
                    }}
                  >
                    Informata të Pagesës
                  </Typography>
                  <MetaItem label="Mënyra Pagesës" value={paymentLabel} />
                  <MetaItem
                    label="Statusi i Pagesës"
                    value={paymentStatusPaid ? "Paguar" : "Pa Paguar"}
                  />
                  <MetaItem label="Tipi i Dokumentit" value="Avans" />
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1.6,
                  p: 1.8,
                  border: "1px solid #d9e6ee",
                  borderRadius: 2,
                  backgroundColor: "#f8fcff",
                  minHeight: 72,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#5d7385",
                    fontWeight: 700,
                    letterSpacing: 0.2,
                    textTransform: "uppercase",
                  }}
                >
                  Komenti
                </Typography>
                <Typography variant="body2" sx={{ color: "#233445", lineHeight: 1.55, mt: 0.7 }}>
                  {data?.Coment || "-"}
                </Typography>
              </Box>
            </SectionCard>

            <SectionCard title="Vlerat dhe TVSH">
              <Box
                sx={{
                  border: "1px solid #dce8ef",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr 1fr 1fr",
                    px: 1.6,
                    py: 1.1,
                    backgroundColor: "#eef5f9",
                    borderBottom: "1px solid #dce8ef",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#47606f" }}>
                    Norma TVSH
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#47606f" }}>
                    Vlera pa TVSH
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#47606f" }}>
                    Vlera e TVSH
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#47606f" }}>
                    Total
                  </Typography>
                </Box>

                {[
                  { rate: "18%", amount: amount18, vat: vat18, total: total18 },
                  { rate: "8%", amount: amount8, vat: vat8, total: total8 },
                  { rate: "0%", amount: amount0, vat: 0, total: total0 },
                ].map((row, index) => (
                  <Box
                    key={row.rate}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr 1fr 1fr",
                      px: 1.6,
                      py: 1.05,
                      borderBottom: index === 2 ? "none" : "1px solid #eef3f7",
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfdff",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#26465f" }}>
                      {row.rate}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                      {formatMoney(row.amount)} EUR
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                      {formatMoney(row.vat)} EUR
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#193b54" }}>
                      {formatMoney(row.total)} EUR
                    </Typography>
                  </Box>
                ))}
              </Box>
            </SectionCard>

            <SectionCard title="Totale">
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1.5 }}>
                {[
                  { label: "Total pa TVSH", value: totalNoVat, tone: "#1d4ed8" },
                  { label: "Total TVSH", value: totalVat, tone: "#0f766e" },
                  { label: "Total me TVSH", value: totalWithVat, tone: "#284945" },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.7,
                      borderRadius: 2,
                      border: "1px solid #dbe7ef",
                      backgroundColor: "#f8fcff",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#607485", fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        mt: 0.55,
                        fontWeight: 800,
                        color: item.tone,
                        fontSize: "1.12rem",
                      }}
                    >
                      {formatMoney(item.value)} EUR
                    </Typography>
                  </Box>
                ))}
              </Box>
            </SectionCard>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3.5, py: 2.25, backgroundColor: "#edf4f8" }}>
        <Button
          onClick={onHide}
          variant="contained"
          sx={{
            background: "linear-gradient(135deg, #1b4e57 0%, #284945 100%)",
            px: 3.5,
            fontWeight: 700,
            borderRadius: 2,
            "&:hover": {
              background: "linear-gradient(135deg, #153f46 0%, #203c39 100%)",
            },
          }}
        >
          Mbyll
        </Button>
      </DialogActions>
    </Dialog>
  );
}
